import { Icon, type IconDefinition } from '@vrplatform/icons';
import { Badge, type MantineSize } from '@mantine/core';
import { type ComponentProps, type PropsWithChildren, forwardRef } from 'react';

type Props = {
  shadow?: MantineSize;
  radius?: string | number;
  color?: string;
  leftIcon?: IconDefinition | (() => React.ReactNode);
  leftIconSize?: number;
  backgroundColor?: string;
  withBorder?: boolean;
  borderColor?: string;
  borderWidth?: number;
  height?: number | string;
  leftIconColor?: ComponentProps<typeof Icon>['color'];
  component?: 'div' | 'button';
  size?: MantineSize;
  fontSize?: number;
  onClick?: (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
  ) => void;
  rightSection?: React.ReactNode;
};

export const Tag = forwardRef<
  HTMLDivElement | HTMLButtonElement,
  PropsWithChildren<Props>
>(
  (
    {
      children,
      shadow,
      radius = 5,
      color,
      leftIcon,
      leftIconSize = 12,
      leftIconColor,
      backgroundColor = '#fff',
      withBorder = true,
      borderWidth,
      height,
      borderColor,
      component,
      onClick,
      fontSize = 12,
      size: initialSize,
      rightSection,
      ...props
    },
    ref
  ) => {
    const size = initialSize || 'md';

    return (
      <Badge
        ref={ref as any}
        variant="default"
        size={size}
        component={component}
        onClick={onClick}
        sx={(theme) => ({
          color,
          borderColor: withBorder
            ? borderColor || theme.colors.neutral[2]
            : 'transparent',
          height: height || (!initialSize ? '1.5rem' : undefined),
          boxShadow: shadow ? theme.shadows[shadow] : undefined,
          borderRadius: radius,
          fontWeight: 400,
          textTransform: 'none',
          paddingInline: '7px',
          borderWidth: borderWidth,
          lineHeight: '1rem',
          backgroundColor,
          fontSize,
          cursor: onClick ? 'pointer' : undefined,
          ':active': onClick
            ? {
                transform: 'translateY(1px)',
              }
            : undefined,
        })}
        leftSection={
          typeof leftIcon === 'string' ? (
            <Icon icon={leftIcon} size={leftIconSize} color={leftIconColor} />
          ) : typeof leftIcon === 'function' ? (
            leftIcon()
          ) : undefined
        }
        rightSection={rightSection}
        {...props}
      >
        {children}
      </Badge>
    );
  }
);
